/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  OFFLINEBANNER.TSX — App-wide offline detection banner                    ║
 * ║  Shows a persistent warning when the user loses internet connectivity     ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [showRecovered, setShowRecovered] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      if (wasOffline) {
        setShowRecovered(true);
        setTimeout(() => setShowRecovered(false), 4000);
      }
    };

    const handleOffline = () => {
      setIsOffline(true);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  return (
    <AnimatePresence>
      {isOffline && (
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-red-900/95 via-red-800/95 to-red-900/95 backdrop-blur-md border-b border-red-500/40 px-4 py-3 shadow-lg shadow-red-900/30"
        >
          <div className="flex items-center justify-center gap-3 max-w-screen-xl mx-auto">
            <span className="text-xl animate-pulse">📡</span>
            <div className="text-center">
              <p className="text-red-100 font-rajdhani font-semibold text-sm">
                You're offline — game data may be stale
              </p>
              <p className="text-red-300/70 text-xs font-mono">
                Transactions cannot be submitted without a network connection
              </p>
            </div>
            <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
          </div>
        </motion.div>
      )}

      {showRecovered && !isOffline && (
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-green-900/95 via-green-800/95 to-green-900/95 backdrop-blur-md border-b border-green-500/40 px-4 py-2.5 shadow-lg"
        >
          <div className="flex items-center justify-center gap-3">
            <span className="text-lg">✅</span>
            <p className="text-green-100 font-rajdhani font-semibold text-sm">
              Connection restored — you're back online
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
